'use client';

import styles from './index.module.scss';
import { pricingPlans } from './data';
import MainButton from "@/app/_components/Shared/MainButton";
import DirhamSymbol from "@/app/_components/Shared/DirhamSymbol";
import { useRouter } from "nextjs-toploader/app";

const TierIcon = ({ tier, isPopular }) => {
  const iconClass = isPopular ? styles.iconWhite : styles.icon;

  if (tier === 'blue') {
    return (
      <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    );
  } else if (tier === 'silver') {
    return (
      <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    );
  } else if (tier === 'gold') {
    return (
      <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    );
  } else if (tier === 'platinum') {
    return (
      <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  } else if (tier === 'enterprise') {
    return (
      <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    );
  }
  return null;
};

const CheckIcon = ({ isPopular }) => (
  <svg
    className={isPopular ? styles.checkIconPopular : styles.checkIcon}
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
);

const StarIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const ArrowIcon = () => (
  <svg className={styles.arrowIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const LockIcon = () => (
  <svg className={styles.lockIcon} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
  </svg>
);

const TagIcon = () => (
  <svg className={styles.tagIcon} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
  </svg>
);

/* ── Tier Card (Blue / Silver / Gold / Platinum) ────────────────────── */
function TierCard({ plan, onAction }) {
  const isPopular = plan.popular;
  const tierClass = styles[`tier_${plan.tier}`] || '';

  return (
    <div className={`${styles.card} ${tierClass} ${isPopular ? styles.cardPopular : ''}`}>
      {isPopular && (
        <div className={styles.popularBadge}>
          <StarIcon className={styles.popularBadgeIcon} />
          Most Popular
        </div>
      )}

      {/* Header */}
      <div className={styles.cardHeader}>
        <div className={`${styles.iconWrapper} ${isPopular ? styles.iconWrapperPopular : ''}`}>
          <TierIcon tier={plan.tier} isPopular={isPopular} />
        </div>
        <div>
          <h3 className={`${styles.cardTitle} ${isPopular ? styles.textWhite : ''}`}>
            {plan.name}
          </h3>
          <p className={`${styles.cardSubtitle} ${isPopular ? styles.textLight : ''}`}>
            {plan.requestRange}
          </p>
        </div>
      </div>

      {/* Price */}
      <div className={`${styles.pricing} ${isPopular ? styles.textWhite : ''}`}>
        <div className={styles.originalPrice}>
          <span className={`${styles.strikethrough} ${isPopular ? styles.textLight : ''}`}>
            AED {plan.originalPrice}
          </span>
          <span className={`${styles.discountBadge} ${isPopular ? styles.discountBadgePopular : ''}`}>
            {plan.discount}
          </span>
        </div>
        <div className={styles.currentPrice}>
          <span className={styles.priceValue}>
            {plan.price}
            <DirhamSymbol size={24} color={isPopular ? '#ffffff' : '#101828'} />
          </span>
          <span className={`${styles.priceUnit} ${isPopular ? styles.textLight : ''}`}>
            {plan.unit}
          </span>
        </div>
      </div>

      {/* Subtitle */}
      <p className={`${styles.tierSubtitle} ${isPopular ? styles.textLight : ''}`}>
        {plan.subtitle}
      </p>

      {/* Features */}
      <div className={`${styles.features} ${isPopular ? styles.textWhite : ''}`}>
        <span className={`${styles.includesLabel} ${isPopular ? styles.textLight : ''}`}>
          Includes:
        </span>
        {plan.features.map((feat, i) => (
          <div key={i} className={styles.feature}>
            <CheckIcon isPopular={isPopular} />
            <span>{feat}</span>
          </div>
        ))}
      </div>

      {/* Pay-as-you-go (Blue tier) */}
      {plan.payAsYouGo && (
        <div className={styles.payAsYouGo}>
          <h4 className={styles.sectionTitle}>{plan.payAsYouGo.title}</h4>
          <div className={styles.sectionBullets}>
            {plan.payAsYouGo.bullets.map((b, i) => (
              <span key={i} className={styles.sectionBullet}>{b}</span>
            ))}
          </div>
          <div className={styles.tierProgression}>
            {plan.payAsYouGo.tiers.map((t, i) => (
              <div key={i} className={styles.tierProgressionItem}>
                <ArrowIcon />
                <span>
                  <strong>{t.range}</strong> = {t.tier}
                </span>
              </div>
            ))}
          </div>
          <p className={styles.sectionNote}>{plan.payAsYouGo.note}</p>
          <p className={styles.sectionRetention}>{plan.payAsYouGo.retention}</p>
        </div>
      )}

      {/* Subscription lock-in (Silver / Gold / Platinum) */}
      {plan.subscription && (
        <div className={`${styles.subscriptionBox} ${isPopular ? styles.subscriptionBoxPopular : ''}`}>
          <LockIcon />
          <div>
            <h4 className={styles.subscriptionTitle}>{plan.subscription.title}</h4>
            <p className={styles.subscriptionSubtitle}>{plan.subscription.subtitle}</p>
            <span className={styles.subscriptionRequests}>{plan.subscription.requests}</span>
          </div>
        </div>
      )}

      {/* Annual prepayment (Silver / Gold / Platinum) */}
      {plan.annual && (
        <div className={`${styles.annualBox} ${isPopular ? styles.annualBoxPopular : ''}`}>
          <TagIcon />
          <div>
            <h4 className={styles.annualTitle}>{plan.annual.title}</h4>
            <p className={styles.annualRequests}>{plan.annual.requests}</p>
            <div className={styles.annualPriceRow}>
              <span className={styles.annualPrice}>
                AED {plan.annual.price} / Request
              </span>
              <span className={styles.annualDiscount}>({plan.annual.discount})</span>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <MainButton
        color={isPopular ? 'white' : 'dark'}
        onClick={onAction}
      >
        {plan.buttonText}
      </MainButton>
    </div>
  );
}

/* ── Enterprise Card ────────────────────────────────────────────────── */
function EnterpriseCard({ plan, onAction }) {
  return (
    <div className={`${styles.enterpriseCard}`}>
      <div className={styles.enterpriseContent}>
        <div className={`${styles.iconWrapper} ${styles.iconWrapperEnterprise}`}>
          <TierIcon tier="enterprise" isPopular={false} />
        </div>
        <div>
          <h3 className={styles.enterpriseTitle}>{plan.name}</h3>
          <p className={styles.enterpriseSubtitle}>{plan.subtitle}</p>
          <p className={styles.enterpriseDescription}>{plan.description}</p>
        </div>
      </div>
      <MainButton color="gradient" onClick={onAction}>
        {plan.buttonText}
      </MainButton>
    </div>
  );
}

/* ── Main Section ───────────────────────────────────────────────────── */
export default function PricingSection() {
  const router = useRouter();

  const handleGetStarted = () => {
    router.push('/pro-services-request');
  };

  const handleContact = () => {
    router.push('/contact');
  };

  const tierPlans = pricingPlans.filter((p) => p.tier !== 'enterprise');
  const enterprisePlan = pricingPlans.find((p) => p.tier === 'enterprise');

  return (
    <section className={styles.container} id="pricing">
      <div className="container-wrapper">
        <div className={styles.header}>
          <div className={styles.badge}>
            <StarIcon className={styles.badgeIcon} />
            NINJAZ PRO Services
          </div>
          <h2 className={styles.title}>PRO Services Pricing</h2>
          <p className={styles.description}>
            Transparent pricing. No hidden fees. Your tier adjusts automatically based on usage.
          </p>
        </div>

        <div className={styles.grid}>
          {tierPlans.map((plan, index) => (
            <TierCard key={index} plan={plan} onAction={handleGetStarted} />
          ))}
        </div>

        {enterprisePlan && (
          <EnterpriseCard plan={enterprisePlan} onAction={handleContact} />
        )}
      </div>
    </section>
  );
}
